import { matchPath } from "react-router-dom"

import {
  ACCOUNT_SETTINGS_BREADCRUMB_LABEL,
  ACCOUNT_SETTINGS_BREADCRUMB_PATH,
  ACCOUNT_SETTINGS_TABS,
  isAccountSettingsPathname,
  resolveAccountSettingsTabId,
} from "@/constants/accountSettingsSidebar"
import {
  NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL,
  getNotificationsCentreCategoryLabel,
  isNotificationsCentrePathname,
  notificationsCentrePath,
  resolveNotificationsCentreCategory,
  resolveNotificationsCentreNotificationId,
} from "@/constants/notificationsCentre"
import type { RouteConfigArray } from "@/types/routes"

const UNIVERSITY_PATH = "/university"

export type BreadcrumbCrumb = {
  label: string
  /** Omit on the current (last) segment. */
  to?: string
}

type FlatRoute = {
  path: string
  name: string
}

function flattenRoutes(routes: RouteConfigArray, layoutPrefix = ""): FlatRoute[] {
  const out: FlatRoute[] = []
  for (const route of routes) {
    if (route.collapse && route.views) {
      out.push(...flattenRoutes(route.views, layoutPrefix))
      continue
    }
    if (route.category && route.views) {
      out.push(...flattenRoutes(route.views, layoutPrefix))
      continue
    }
    if (!route.path) {
      continue
    }
    const fullPath = `${layoutPrefix}${route.path}`.replace(/\/{2,}/g, "/")
    out.push({ path: fullPath, name: route.name })
  }
  return out
}

function findMatchingRoute(
  flatRoutes: FlatRoute[],
  pathname: string
): FlatRoute | null {
  const sorted = [...flatRoutes].sort((a, b) => b.path.length - a.path.length)
  for (const route of sorted) {
    const match = matchPath(pathname, { path: route.path, exact: true })
    if (match) {
      return route
    }
  }
  return null
}

function parentPathForRoute(routePath: string): string | null {
  if (routePath.includes(":")) {
    const withoutParams = routePath.replace(/\/:[^/]+/g, "")
    if (withoutParams && withoutParams !== routePath) {
      return withoutParams
    }
  }
  const segments = routePath.split("/").filter(Boolean)
  if (segments.length <= 1) {
    return null
  }
  return `/${segments.slice(0, -1).join("/")}`
}

function buildRouteTrail(
  flatRoutes: FlatRoute[],
  pathname: string
): BreadcrumbCrumb[] {
  const matched = findMatchingRoute(flatRoutes, pathname)
  if (!matched) {
    return [{ label: "Dashboard" }]
  }

  const crumbs: BreadcrumbCrumb[] = []
  const parentPath = parentPathForRoute(matched.path)
  if (parentPath) {
    const parent = flatRoutes.find((r) => r.path === parentPath)
    if (parent) {
      crumbs.push({ label: parent.name, to: parent.path })
    }
  }

  crumbs.push({ label: matched.name })
  return crumbs
}

function isUniversityPathname(pathname: string): boolean {
  return (
    pathname === UNIVERSITY_PATH || pathname.startsWith(`${UNIVERSITY_PATH}/`)
  )
}

function buildUniversityBreadcrumbTrail(pathname: string): BreadcrumbCrumb[] {
  if (pathname === UNIVERSITY_PATH) {
    return [{ label: "University" }]
  }

  const courseMatch = matchPath(pathname, {
    path: `${UNIVERSITY_PATH}/course/:courseId`,
    exact: true,
  })
  if (courseMatch) {
    return [
      { label: "University", to: UNIVERSITY_PATH },
      { label: "Course Overview" },
    ]
  }

  const lessonMatch = matchPath<{ courseId: string }>(pathname, {
    path: `${UNIVERSITY_PATH}/course/:courseId/module/:moduleId/lesson/:lessonId`,
    exact: true,
  })
  if (lessonMatch?.params.courseId) {
    return [
      { label: "University", to: UNIVERSITY_PATH },
      {
        label: "Course Overview",
        to: `${UNIVERSITY_PATH}/course/${lessonMatch.params.courseId}`,
      },
      { label: "Course Learn" },
    ]
  }

  return [{ label: "University" }]
}

/** Breadcrumb trail after the brand root (Tessera → …). */
export function getBreadcrumbTrail(
  routes: RouteConfigArray,
  pathname: string,
  search: string
): BreadcrumbCrumb[] {
  if (isAccountSettingsPathname(pathname)) {
    const tabId = resolveAccountSettingsTabId(pathname, search)
    const tab = ACCOUNT_SETTINGS_TABS.find((t) => t.id === tabId)
    return [
      {
        label: ACCOUNT_SETTINGS_BREADCRUMB_LABEL,
        to: ACCOUNT_SETTINGS_BREADCRUMB_PATH,
      },
      {
        label: tab?.label ?? "General",
      },
    ]
  }

  if (isNotificationsCentrePathname(pathname)) {
    const category = resolveNotificationsCentreCategory(pathname)
    const notificationId = resolveNotificationsCentreNotificationId(pathname)
    if (notificationId) {
      const trail: BreadcrumbCrumb[] = [
        {
          label: NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL,
          to: notificationsCentrePath(),
        },
      ]
      if (category) {
        trail.push({
          label: getNotificationsCentreCategoryLabel(category),
          to: `/notifications/category/${encodeURIComponent(category)}`,
        })
      }
      trail.push({ label: "Details" })
      return trail
    }
    if (category) {
      return [
        {
          label: NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL,
          to: notificationsCentrePath(),
        },
        {
          label: getNotificationsCentreCategoryLabel(category),
          to: `/notifications/category/${encodeURIComponent(category)}`,
        },
      ]
    }
    return [{ label: NOTIFICATIONS_CENTRE_BREADCRUMB_LABEL }]
  }

  if (isUniversityPathname(pathname)) {
    return buildUniversityBreadcrumbTrail(pathname)
  }

  const flatRoutes = flattenRoutes(routes)
  return buildRouteTrail(flatRoutes, pathname)
}
